import Table from "@/shared/components/Table";
import { useTranslation } from "react-i18next";
import { COMMON_BUTTONS } from "@/config/actionButtonConfigs";
import ActionButtons from "@/shared/components/ActionButtons";
import { useCallback, useEffect, useState } from "react";
import NewTableForm from "./NewTableForm";
import NewTableRowForm from "./NewTableRowForm";
import DeleteModal from "@/shared/components/DeleteModal";
import useModal from "@/shared/hooks/useModal";
import { lengthOfServiceService } from "../services/lengthOfServiceService";
import { extractErrorMessage } from "@/shared/services/blockingError";
import { notifyError, notifySuccess } from "@/config/tostifyConfig/tostifyConfig";

const buildTableRecordId = (record) => (
    `${record.tblClassId}-${record.fromDate}-${record.catWorkId}`
);

const mapTblClassList = (items, t) => (
    Array.isArray(items)
        ? items.map((item) => ({
            id: buildTableRecordId(item),
            recordId: item.id,
            tblClassId: item.tblClassId,
            fromDate: item.fromDate,
            catWork: item.catWork,
            catWorkId: item.catWorkId,
            active: item.active,
            additionalClassTable: '',
            tableNumber: item.tblClassId,
            categoryOfWork: item.catWork,
            activeLabel: item.active ? t('common.yes') : t('common.no'),
        }))
        : []
);

const mapTblClassDetails = (items) => (
    Array.isArray(items)
        ? items.map((item) => ({
            id: item.id,
            recordId: item.id,
            tblClassId: item.tblClassId,
            years: item.years,
            percent: item.percent,
            tableNumber: item.tblClassId,
            yearsOfService: item.years,
        }))
        : []
);

export default function LengthOfService() {
    const { t } = useTranslation();
    const [tableData, setTableData] = useState([]);
    const [rowData, setRowData] = useState([]);
    const [tableLoading, setTableLoading] = useState(false);
    const [rowLoading, setRowLoading] = useState(false);
    const [tableError, setTableError] = useState(null);
    const [rowError, setRowError] = useState(null);
    const [selectedTableRecord, setSelectedTableRecord] = useState(null);
    const [selectedRowRecord, setSelectedRowRecord] = useState(null);
    const [updatingActiveRecordId, setUpdatingActiveRecordId] = useState(null);
    const {
        modalState: tableModalState,
        handleOpenModal: openTableModal,
        handleCloseModal: closeTableModal,
    } = useModal();
    const {
        modalState: rowModalState,
        handleOpenModal: openRowModal,
        handleCloseModal: closeRowModal,
    } = useModal();

    const deleteWarningMessage = t(
        'Compensations as per labor contracts.lengthOfService.deleteWarning'
    );

    const firstColumns = [
        {
            key: "additionalClassTable",
            label: t("Compensations as per labor contracts.lengthOfService.table.headers.additionalClassTable"),
        },
        {
            key: "tableNumber",
            label: t("Compensations as per labor contracts.lengthOfService.table.headers.tableNumber"),
        },
        {
            key: "fromDate",
            label: t("Compensations as per labor contracts.lengthOfService.table.headers.fromDate"),
        },
        {
            key: "categoryOfWork",
            label: t("Compensations as per labor contracts.lengthOfService.table.headers.categoryOfWork"),
        },
        {
            key: "activeLabel",
            label: t("Compensations as per labor contracts.lengthOfService.table.headers.active"),
        },
    ];

    const secondColumns = [
        {
            key: "tableNumber",
            label: t("Compensations as per labor contracts.lengthOfService.table.headers.tableNumber"),
        },
        {
            key: "yearsOfService",
            label: t("Compensations as per labor contracts.lengthOfService.table.headers.yearsOfService"),
        },
        {
            key: "percent",
            label: t("Compensations as per labor contracts.lengthOfService.table.headers.percent"),
        },
    ];

    const fetchTblClassList = useCallback(async () => {
        setTableLoading(true);
        setTableError(null);
        try {
            const response = await lengthOfServiceService.getTblClassList();
            setTableData(mapTblClassList(response, t));
        } catch (error) {
            console.error('Error fetching tbl-class list:', error);
            const message = error?.response?.data?.message || error?.message || t('common.errorMessage');
            setTableError(message);
            setTableData([]);
            notifyError(message);
        } finally {
            setTableLoading(false);
        }
    }, [t]);

    const fetchTblClassDetails = useCallback(async (record) => {
        if (!record?.tblClassId || !record?.fromDate || record?.catWorkId == null) {
            setRowData([]);
            return;
        }

        setRowLoading(true);
        setRowError(null);
        try {
            const response = await lengthOfServiceService.getTblClassDetails({
                tblClassId: record.tblClassId,
                fromDate: record.fromDate,
                catWorkId: record.catWorkId,
            });
            setRowData(mapTblClassDetails(response));
        } catch (error) {
            console.error('Error fetching tbl-class details:', error);
            const message = error?.response?.data?.message || error?.message || t('common.errorMessage');
            setRowError(message);
            setRowData([]);
            notifyError(message);
        } finally {
            setRowLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchTblClassList();
    }, [fetchTblClassList]);

    const handleTableRowSelect = (index) => {
        const record = tableData[index];
        if (!record) {
            return;
        }

        setSelectedTableRecord(record);
        setSelectedRowRecord(null);
        fetchTblClassDetails(record);
    };

    const handleRowSelect = (index) => {
        const record = rowData[index];
        if (!record) {
            return;
        }
        setSelectedRowRecord(record);
    };

    const handleActiveToggle = async (record, nextActive, event) => {
        event?.stopPropagation();

        if (!record?.recordId) {
            notifyError(t('common.errorMessage'));
            return;
        }

        setUpdatingActiveRecordId(record.id);
        try {
            await lengthOfServiceService.updateTblClassActive(record.recordId, nextActive);

            const updateRecord = (item) => (
                item.id === record.id
                    ? {
                        ...item,
                        active: nextActive,
                        activeLabel: nextActive ? t('common.yes') : t('common.no'),
                    }
                    : item
            );

            setTableData((prev) => prev.map(updateRecord));
            setSelectedTableRecord((prev) => (
                prev?.id === record.id ? updateRecord(prev) : prev
            ));
            notifySuccess(t('common.updatedSuccessfully'));
        } catch (error) {
            console.error('Error updating tbl-class active status:', error);
            notifyError(extractErrorMessage(error));
        } finally {
            setUpdatingActiveRecordId(null);
        }
    };

    const handleTableFormSuccess = () => {
        setSelectedTableRecord(null);
        setSelectedRowRecord(null);
        setRowData([]);
        fetchTblClassList();
    };

    const handleTableModalClose = () => {
        closeTableModal();
    };

    const handleRowFormSuccess = () => {
        setSelectedRowRecord(null);
        if (selectedTableRecord) {
            fetchTblClassDetails(selectedTableRecord);
        }
    };

    const handleOpenRowCreateModal = () => {
        if (!selectedTableRecord?.recordId) {
            notifyError(t('common.selectRecord'));
            return;
        }
        openRowModal('create');
    };

    const handleOpenRowEditModal = () => {
        if (!selectedRowRecord?.recordId) {
            notifyError(t('common.selectRecord'));
            return;
        }
        openRowModal('edit');
    };

    const handleConfirmTableDelete = async () => {
        try {
            await lengthOfServiceService.deleteTblClass(selectedTableRecord.recordId);
            closeTableModal();
            setSelectedTableRecord(null);
            setSelectedRowRecord(null);
            setRowData([]);
            fetchTblClassList();
            notifySuccess(t('common.deletedSuccessfully'));
        } catch (error) {
            console.error('Error deleting tbl-class:', error);
            notifyError(extractErrorMessage(error));
        }
    };

    const handleConfirmRowDelete = async () => {
        try {
            await lengthOfServiceService.deleteTblClass(selectedRowRecord.recordId);
            closeRowModal();
            setSelectedRowRecord(null);
            if (selectedTableRecord) {
                fetchTblClassDetails(selectedTableRecord);
            }
            notifySuccess(t('common.deletedSuccessfully'));
        } catch (error) {
            console.error('Error deleting tbl-class detail:', error);
            notifyError(extractErrorMessage(error));
        }
    };

    const tableActionButtons = (
        <ActionButtons
            buttons={[
                { ...COMMON_BUTTONS.ADD, onClick: () => openTableModal('create') },
                { ...COMMON_BUTTONS.DELETE, onClick: () => openTableModal('delete') },
            ]}
            selectedRecord={selectedTableRecord}
        />
    );

    const rowActionButtons = (
        <ActionButtons
            buttons={[
                { ...COMMON_BUTTONS.ADD, onClick: handleOpenRowCreateModal },
                { ...COMMON_BUTTONS.EDIT, onClick: handleOpenRowEditModal },
                { ...COMMON_BUTTONS.DELETE, onClick: () => openRowModal('delete') },
            ]}
            selectedRecord={selectedRowRecord}
        />
    );

    return (
        <div className="flex-column">
            <div className="flex-row position-center">
                <h1 className="title">{t("Compensations as per labor contracts.lengthOfService.title")}</h1>
            </div>
            <div className="flex-row">
                <Table
                    columns={firstColumns}
                    data={tableData}
                    loading={tableLoading}
                    error={tableError}
                    showPageSizeSelector={false}
                    showPagination={false}
                    actionButtons={tableActionButtons}
                    handleCheckboxChange={handleTableRowSelect}
                    selectedData={selectedTableRecord?.id}
                    renderCell={(item, column) => {
                        if (column.key === 'activeLabel') {
                            return (
                                <div
                                    className="input-checkbox-wrapper"
                                    onClick={(event) => event.stopPropagation()}
                                >
                                    <input
                                        type="checkbox"
                                        checked={Boolean(item.active)}
                                        disabled={updatingActiveRecordId === item.id}
                                        onChange={(event) => handleActiveToggle(item, event.target.checked, event)}
                                        title={t('common.edit')}
                                    />
                                </div>
                            );
                        }

                        return item[column.key];
                    }}
                />
                <Table
                    columns={secondColumns}
                    data={rowData}
                    loading={rowLoading}
                    error={rowError}
                    showPageSizeSelector={false}
                    showPagination={false}
                    actionButtons={rowActionButtons}
                    handleCheckboxChange={handleRowSelect}
                    selectedData={selectedRowRecord?.id}
                />
            </div>

            {tableModalState.visible && tableModalState.type === 'create' && (
                <NewTableForm
                    onClose={handleTableModalClose}
                    type={tableModalState.type}
                    onSuccess={handleTableFormSuccess}
                />
            )}
            {tableModalState.visible && tableModalState.type === 'delete' && (
                <DeleteModal
                    onClose={closeTableModal}
                    onConfirm={handleConfirmTableDelete}
                    selectedData={selectedTableRecord?.recordId}
                    name={selectedTableRecord?.tableNumber}
                    entityType="record"
                    message={deleteWarningMessage}
                />
            )}
            {rowModalState.visible && (rowModalState.type === 'create' || rowModalState.type === 'edit') && (
                <NewTableRowForm
                    onClose={closeRowModal}
                    type={rowModalState.type}
                    parentRecordId={selectedTableRecord?.recordId}
                    record={rowModalState.type === 'edit' ? selectedRowRecord : null}
                    onSuccess={handleRowFormSuccess}
                />
            )}
            {rowModalState.visible && rowModalState.type === 'delete' && (
                <DeleteModal
                    onClose={closeRowModal}
                    onConfirm={handleConfirmRowDelete}
                    selectedData={selectedRowRecord?.recordId}
                    name={selectedRowRecord?.yearsOfService}
                    entityType="record"
                    message={deleteWarningMessage}
                />
            )}
        </div>
    );
}
